import { DamageType, PetAbilitySlot, PetHit, PetRule, SimConstants, StrikeTarget, hitTouches } from "@highmarch/domain";

/**
 * The numbers of one pet ability for the pet sheet (Pets v2/v3, docs/PETS.md), read from a run copy of the pet
 * (domain meta/petAbilities.ts petRule) so the sheet shows exactly what a battle would play. Display numbers (seconds,
 * cells, damage floored) are plain numbers for the presentation; `id` is the ability's kind.
 */

/** An effect block in display numbers (null: none of it). */
export interface PetEffectNumbers {
  readonly damage: number;
  readonly damageType: "physical" | "magic" | "fire";
  readonly burn: { readonly perSecond: number; readonly seconds: number } | null;
  readonly slow: { readonly percent: number; readonly seconds: number } | null;
  readonly stun: { readonly seconds: number; readonly bossSeconds: number } | null;
  readonly mark: { readonly seconds: number; readonly takenPercent: number; readonly pointsPercent: number } | null;
  readonly dazzleSeconds: number;
}

/** One ability's numbers, by its kind (the pup's four, then the Pets v3 kinds). */
export type PetAbilityNumbers =
  | { readonly id: "bite"; readonly damage: number; readonly everySeconds: number; readonly reach: number }
  | { readonly id: "muster"; readonly spawnCutPercent: number }
  | { readonly id: "rallyBark"; readonly everySeconds: number; readonly forSeconds: number; readonly reach: number; readonly damagePercent: number; readonly guardPercent: number }
  | { readonly id: "howl"; readonly reach: number; readonly stunSeconds: number; readonly bossStunSeconds: number }
  /** An attack: the raider nearest the flag within `reach` (ground, any, or a flyer first), `airMultPercent` on a flyer. */
  | { readonly id: "strike"; readonly damage: number; readonly everySeconds: number; readonly reach: number; readonly targets: "ground" | "any" | "airFirst"; readonly airMultPercent: number; readonly effect: PetEffectNumbers }
  /** A passive within `reach` of the flag. */
  | {
    readonly id: "aura"; readonly reach: number; readonly towerAirPercent: number; readonly burningTakenPercent: number; readonly troopGuardPercent: number;
    readonly heal: { readonly everySeconds: number; readonly percent: number } | null; readonly killPointsPercent: number;
  }
  /** Every `everySeconds`: raiders within `reach` (flyers when `air`; the one with most health when `strongest`) take `effect`; troops a shield and a heal. */
  | {
    readonly id: "pulse"; readonly everySeconds: number; readonly reach: number; readonly air: boolean; readonly strongest: boolean;
    readonly troopShieldPercent: number; readonly shieldSeconds: number; readonly troopHealPercent: number; readonly effect: PetEffectNumbers | null;
  }
  /** Once a wave, the first raider within `reach`: those there (or the strongest) take `effect`. */
  | { readonly id: "signature"; readonly reach: number; readonly air: boolean; readonly strongest: boolean; readonly effect: PetEffectNumbers };

const sec = (t: number): number => t / SimConstants.TicksPerSecond;
const TYPES = { [DamageType.Physical]: "physical", [DamageType.Magic]: "magic", [DamageType.Burn]: "fire" } as const;
const TARGETS = { [StrikeTarget.Ground]: "ground", [StrikeTarget.Any]: "any", [StrikeTarget.AirFirst]: "airFirst" } as const;
const PERCENT = 100;

export function effectNumbers(h: PetHit): PetEffectNumbers {
  return {
    damage: Math.floor(h.damage.toNumber()), damageType: TYPES[h.damageType],
    burn: h.burnTicks > 0 ? { perSecond: Math.round(h.burnPerTick.toNumber() * SimConstants.TicksPerSecond), seconds: sec(h.burnTicks) } : null,
    slow: h.slowTicks > 0 ? { percent: Math.round(h.slow.toNumber() * PERCENT), seconds: sec(h.slowTicks) } : null,
    stun: h.stunTicks > 0 ? { seconds: sec(h.stunTicks), bossSeconds: sec(h.bossStunTicks) } : null,
    mark: h.markTicks > 0 ? { seconds: sec(h.markTicks), takenPercent: h.markTakenPercent, pointsPercent: h.markPointsPercent } : null,
    dazzleSeconds: sec(h.dazzleTicks),
  };
}

/** The numbers of `slot` in a run rule, or null when the rule lacks it (still locked). */
export function abilityNumbers(rule: PetRule, slot: PetAbilitySlot): PetAbilityNumbers | null {
  switch (slot) {
    case "attack": {
      if (rule.bite) return { id: "bite", damage: Math.floor(rule.bite.damage.toNumber()), everySeconds: sec(rule.bite.intervalTicks), reach: rule.bite.reach.toNumber() };
      const s = rule.strike;
      return s ? { id: "strike", damage: Math.floor(s.hit.damage.toNumber()), everySeconds: sec(s.intervalTicks), reach: s.reach.toNumber(), targets: TARGETS[s.targets], airMultPercent: s.airMultPercent, effect: effectNumbers(s.hit) } : null;
    }
    case "passive": {
      if (rule.spawnCutPercent > 0) return { id: "muster", spawnCutPercent: rule.spawnCutPercent };
      const a = rule.aura;
      return a ? {
        id: "aura", reach: a.reach.toNumber(), towerAirPercent: a.towerAirPercent, burningTakenPercent: a.burningTakenPercent, troopGuardPercent: a.troopGuardPercent,
        heal: a.healEveryTicks > 0 ? { everySeconds: sec(a.healEveryTicks), percent: a.healPercent } : null, killPointsPercent: a.killPointsPercent,
      } : null;
    }
    case "pulse": {
      const b = rule.bark, p = rule.pulse;
      if (b) return { id: "rallyBark", everySeconds: sec(b.everyTicks), forSeconds: sec(b.durationTicks), reach: b.reach.toNumber(), damagePercent: b.damagePercent, guardPercent: b.guardPercent };
      return p ? {
        id: "pulse", everySeconds: sec(p.everyTicks), reach: p.reach.toNumber(), air: p.air, strongest: p.strongest, troopShieldPercent: p.troopShieldPercent,
        shieldSeconds: sec(p.shieldTicks), troopHealPercent: p.troopHealPercent, effect: hitTouches(p.hit) ? effectNumbers(p.hit) : null,
      } : null;
    }
    case "signature": {
      if (rule.howl) return { id: "howl", reach: rule.howl.reach.toNumber(), stunSeconds: sec(rule.howl.stunTicks), bossStunSeconds: sec(rule.howl.bossStunTicks) };
      const s = rule.signature;
      return s ? { id: "signature", reach: s.reach.toNumber(), air: s.air, strongest: s.strongest, effect: effectNumbers(s.hit) } : null;
    }
  }
}
